import React from 'react';
import styled from 'styled-components';
import carouselImages from './carousel-images';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper';
import Imgix from 'react-imgix';
import breakpoints from '@/constants/breakpoints';
import useHover from '@/hooks/useHover';

import 'swiper/swiper-bundle.min.css';
import RoundedArrow from '../../common/RoundedArrow';

const imgixURL = 'https://theinside.imgix.net/homepage/rebrand';

const carouselBreakpoints = {
  [breakpoints['tablet']]: {
    slidesPerView: 3.87,
    slidesOffsetBefore: 21
  },
  [breakpoints['desktop']]: {
    slidesPerView: 4,
    slidesOffsetBefore: 0
  },
  [breakpoints['large-desktop']]: {
    slidesPerView: 5,
    slidesOffsetBefore: 0
  },
  [breakpoints['xlarge-desktop']]: {
    slidesPerView: 6,
    slidesOffsetBefore: 0
  }
};

function ShopByCategory() {
  return (
    <Container>
      <Header>
        <Title>Shop by Category</Title>
        <ShopAll href="/categories">Shop All</ShopAll>
      </Header>

      <div style={{ position: 'relative' }}>
        <Swiper
          spaceBetween={20}
          slidesPerView={1.9}
          slidesOffsetBefore={17}
          slidesPerGroup={1}
          simulateTouch={false}
          className="category-slider"
          navigation={{
            nextEl: `${NextButton}`,
            prevEl: `${PrevButton}`
          }}
          breakpoints={carouselBreakpoints}
          modules={[Navigation]}
        >
          {carouselImages.map(imageCard => (
            <SwiperSlide key={imageCard.label}>
              <Card {...imageCard} />
            </SwiperSlide>
          ))}
        </Swiper>

        <NavButtons>
          <PrevButton>
            <RoundedArrow direction="left" />
          </PrevButton>
          <NextButton>
            <RoundedArrow />
          </NextButton>
        </NavButtons>
      </div>
    </Container>
  );
}

function Card({ label, image, url, hoverImg }) {
  const [hoverRef, isHovered] = useHover();

  return (
    <a
      className={`category-slide ${!hoverImg && 'zoom-img'}`}
      href={url}
      ref={hoverRef}
    >
      <Imgix
        sizes={`
          (max-width: 768px) 110px,
          172px
        `}
        imgixParams={{ auto: 'compress,format', fit: 'crop', ar: '1.775:1' }}
        src={isHovered && hoverImg ? hoverImg : `${imgixURL}/${image}`}
        htmlAttributes={{
          alt: label,
          loading: 'lazy'
        }}
      />
      <Label>{label}</Label>
    </a>
  );
}

const Container = styled.div`
  margin: 0 auto 34px;
  max-width: 1620px;

  width: 100%;
  a:hover {
    text-decoration: none;
  }

  @media (min-width: ${breakpoints['desktop']}px) {
    width: 82%;
  }

  @media (min-width: ${breakpoints['desktop']}px) {
    margin-bottom: 64px;
  }

  .category-slider {
    a:hover {
      text-decoration: none;
    }
  }

  .category-slide {
    padding-top: 35px;
    background: #f8f9fb;
    text-align: center;
    display: block;

    @media (min-width: ${breakpoints['tablet']}px) {
      padding-top: 75px;
    }

    @media (min-width: ${breakpoints['large-desktop']}px) {
      padding-top: 65px;
    }

    img {
      width: 75%;
    }

    &.zoom-img:hover {
      img {
        transition: transform 0.5s ease;
        transform: scale(1.25);
      }
    }
  }
`;

const Label = styled.p`
  padding: 27px 10px 21px;
  font-family: ${({ theme }) => theme.fonts.secondary.family};
  font-size: 16px;
  font-weight: 300;
  letter-spacing: -0.48px;
  color: #202020;

  @media (min-width: ${breakpoints['tablet']}px) {
    padding: 41px 10px 38px;
  }

  @media (min-width: ${breakpoints['desktop']}px) {
    font-size: 20px;
    line-height: 0.9;
    letter-spacing: -0.4px;
  }

  @media (min-width: ${breakpoints['large-desktop']}px) {
    font-size: 24px;
    line-height: normal;
    letter-spacing: -0.48px;
    min-height: 60px;
  }

  @media (min-width: 1510px) {
    min-height: unset;
  }

  @media (min-width: ${breakpoints['xlarge-desktop']}px) {
    min-height: 60px;
  }

  @media (min-width: 1810px) {
    min-height: unset;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  margin-left: 21px;
  margin-bottom: 18.5px;

  @media (min-width: ${breakpoints['desktop']}px) {
    margin-left: 0;
  }
`;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.fonts.secondary.family};
  font-size: 24px;
  font-weight: 300;
  letter-spacing: -0.48px;
  color: ${props => props.theme.colors.charcoal};
  margin-right: 8.5px;
  margin-bottom: 0;

  @media (min-width: ${breakpoints['tablet']}px) {
    font-size: 28px;
    letter-spacing: -0.56px;
    margin-right: 10.6px;
  }

  @media (min-width: ${breakpoints['desktop']}px) {
    font-size: 38px;
    letter-spacing: -0.76px;
    margin-right: 11.4px;
  }

  @media (min-width: ${breakpoints['medium-desktop']}px) {
    font-size: 45px;
    letter-spacing: -1.5px;
    margin-right: 18.2px;
  }
`;

const ShopAll = styled.a`
  cursor: pointer;
  font-family: ${({ theme }) => theme.fonts.primary};
  color: #9F6A41;
  font-size: 16px;
  text-decoration: underline;
  margin-bottom: 10px;

  &:hover {
    text-decoration: none;
    color: #9F6A41;
  }

  media (min-width: ${breakpoints['tablet']}px) {
    font-size: 16px;
  }

  @media (min-width: ${breakpoints['desktop']}px) {
    font-size: 20px;
  }
`;

const NavButtons = styled.div`
  display: none;
  position: absolute;
  top: 50%;
  width: 100%;

  @media (min-width: ${breakpoints['desktop']}px) {
    display: block;
  }

  @media (min-width: ${breakpoints['large-desktop']}px) {
    transform: translateY(-30px);
  }

  a {
    margin: 0;
    cursor: pointer;
    position: absolute;
    z-index: 1;
  }

  a:nth-child(1) {
    left: -75px;
  }
  a:nth-child(2) {
    right: -75px;
  }

  .swiper-button-disabled {
    display: none;
  }
`;

const PrevButton = styled.a``;
const NextButton = styled.a``;

export default ShopByCategory;
